import { vaultFailure, reportVaultFailure, onVaultFailure } from './storageHealth';
import { EncryptedDatabase, type EncryptedTable } from './encryptedDatabase';

const database = new EncryptedDatabase('fusics-browser-state', { values: 'key' }) as EncryptedDatabase & { values: EncryptedTable<{ key: string; value: string }> };
const cache = new Map<string, string>();
let initialized = false;
let initialization: Promise<void> | undefined;
let pending: Promise<void> = Promise.resolve();

export function isStudentStorageKey(key: string): boolean {
  return key.startsWith('lesson-') || key === 'pwa-store' || key === 'fusics-statistics-device-id' || key === 'fusics-local-learning-owner'
    || key.startsWith('fusics-demo-progress-') || key.startsWith('fusics-demo-tour-')
    || key === 'fusics-demo-journey-v1' || key.startsWith('fusics.practice.')
    || key.startsWith('fusics.gym8-lab.') || key.startsWith('fusics.bottle.')
    || key.startsWith('fusics-class-session:') || key.startsWith('fusics-lesson-option-seed:') || key.startsWith('fusics-series-quiz-order:') || key.startsWith('lp_gym') || key.includes('magnus-effect') || key.includes('lunar-jump') || key.includes('planetary-rotation')
    || key.startsWith('fusics-unlock:');
}

export function initializeStudentStorage(): Promise<void> {
  initialization ??= (async () => {
    await database.ready();
    for (const row of await database.values.toArray()) cache.set(row.key, row.value);
    // Commit and verify each legacy item before removing its plaintext copy.
    for (const storage of [window.localStorage, window.sessionStorage]) {
      for (const key of Array.from({ length: storage.length }, (_, index) => storage.key(index)).filter((key): key is string => key !== null && isStudentStorageKey(key))) {
        const legacy = storage.getItem(key);
        if (legacy === null) continue;
        if (!cache.has(key)) {
          await database.values.put({ key, value: legacy });
          if ((await database.values.get(key))?.value !== legacy) throw new Error('Der bisherige Lernstand konnte nicht sicher übernommen werden.');
          cache.set(key, legacy);
        }
        storage.removeItem(key);
      }
    }
    initialized = true;
  })();
  return initialization;
}

function requireReady() {
  if (!initialized) throw new Error('Der lokale Lerntresor ist noch nicht geöffnet.');
}
function queue(operation: () => Promise<unknown>) {
  pending = pending.then(() => { if (vaultFailure()) throw vaultFailure(); return operation(); }).then(() => undefined).catch(reportVaultFailure);
}

/** Synchronous UI facade; persistence is encrypted and failures are surfaced globally. */
export const studentStorage = {
  get length() { requireReady(); return cache.size; },
  key(index: number) { requireReady(); return [...cache.keys()][index] ?? null; },
  getItem(key: string) { requireReady(); return cache.get(key) ?? null; },
  setItem(key: string, value: string) {
    requireReady();
    if (vaultFailure()) throw vaultFailure();
    cache.set(key, String(value));
    queue(() => database.values.put({ key, value: String(value) }));
  },
  clear() { requireReady(); if (vaultFailure()) throw vaultFailure(); cache.clear(); queue(() => database.values.clear()); },
  removeItem(key: string) { requireReady(); if (vaultFailure()) throw vaultFailure(); cache.delete(key); queue(() => database.values.delete(key)); },
};
export async function flushStudentStorage() { await pending; if (vaultFailure()) throw vaultFailure(); }
export function studentStorageError() { return vaultFailure()?.message ?? null; }
export function onStudentStorageError(listener: () => void) { return onVaultFailure(listener); }
export function studentStorageSnapshot() { requireReady(); return Object.fromEntries(cache); }
export function refreshStudentStorageSnapshot(values: Record<string, string>) {
  for (const [key, value] of Object.entries(values)) cache.set(key, value);
}

export function replaceStudentStorageCache(values: Record<string, string>) {
  cache.clear();
  refreshStudentStorageSnapshot(values);
}
